import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/GlobalStyles';
import { Modal } from './Modal';
import { Button } from './Button';

const CalendarContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px ${colors.shadow};
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const MonthTitle = styled.h2`
  color: ${colors.primary};
  margin: 0;
`;

const NavigationButton = styled.button`
  background: ${colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${colors.primaryDark};
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  margin-bottom: 10px;
`;

const WeekDay = styled.div`
  padding: 10px;
  text-align: center;
  font-weight: bold;
  color: ${colors.primary};
  background: ${colors.secondary};
  font-size: 14px;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: ${colors.border};
`;

const CalendarDay = styled.div.attrs(props => ({
  $isOtherMonth: undefined,
  $isToday: undefined,
}))`
  min-height: 120px;
  padding: 8px;
  background: white;
  border: 1px solid ${colors.border};
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${colors.secondary};
  }

  ${props => props.$isOtherMonth && `
    background: ${colors.background};
    color: ${colors.textLight};
  `}

  ${props => props.$isToday && `
    background: ${colors.success};
    color: white;
    font-weight: bold;
  `}
`;

const DayNumber = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
  font-size: 14px;
`;

const ClassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ClassItem = styled.div`
  background: ${props => {
    if (props.$isPast) return colors.textLight;
    if (props.isFull) return colors.textLight;
    if (props.hasReservation) return colors.success;
    return colors.primary;
  }};
  color: white;
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 10px;
  cursor: ${props => props.$isPast ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$isPast ? 0.5 : 1};
  transition: opacity 0.3s ease;

  &:hover {
    opacity: ${props => props.$isPast ? 0.5 : 0.8};
  }
`;

const ClassTitle = styled.div`
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClassTime = styled.div`
  font-size: 9px;
  opacity: 0.9;
`;

const PastBadge = styled.div`
  font-size: 8px;
  font-weight: bold;
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.9);
`;

const InstructorPhoto = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 2px 8px ${colors.shadow};
  margin-bottom: 15px;
`;

const ClassDetails = styled.div`
  margin-bottom: 20px;
`;

const DetailRow = styled.div`
  margin-bottom: 10px;
  font-size: 14px;
  color: ${colors.text};
`;

const LocationLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.primaryDark};
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: ${props => {
    if (props.type === 'full') return colors.error;
    if (props.type === 'reserved') return colors.success;
    if (props.type === 'available') return colors.primary;
    return colors.secondary;
  }};
  color: white;
  margin-left: 8px;
`;

const monthNames = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const weekDayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

export const MemberCalendarView = ({ 
  classes = [], 
  myReservations = [], 
  onReservationClick 
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedClass, setSelectedClass] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Get start of week (Monday)
  const getStartOfWeek = (date) => {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day; // If Sunday, go back 6 days, else go to Monday
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const hasActiveReservation = (classId, classDate) => {
    return myReservations.some(r => 
      r.classId === classId && 
      r.classDate === classDate &&
      (r.status === 'pending' || r.status === 'approved')
    );
  };

  const getReservationStatus = (classId, classDate) => {
    const reservation = myReservations.find(r => 
      r.classId === classId && 
      r.classDate === classDate &&
      (r.status === 'pending' || r.status === 'approved')
    );
    return reservation?.status;
  };

  // Check if a class is in the past
  const isClassPast = (classDate, classTime) => {
    const now = new Date();
    const classDateTime = new Date(`${classDate}T${classTime || '00:00'}`);
    return classDateTime < now;
  };

  const getClassesForDate = (date) => {
    // Format date as YYYY-MM-DD in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return classes.filter(classItem => {
      if (classItem.isRecurring) {
        // Tekrarlı dersler için gün kontrolü ve başlangıç tarihi kontrolü
        const classDate = new Date(classItem.date + 'T00:00:00');
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        
        // Sadece ders başlangıç tarihinden sonraki aynı gün derslerini göster
        return classDate.getDay() === date.getDay() && checkDate >= classDate;
      } else {
        return classItem.date === dateStr;
      }
    });
  };

  const handleClassClick = (classItem, displayDate) => {
    // Create a new class object with the actual display date (important for recurring classes)
    const classWithDate = {
      ...classItem,
      date: displayDate // Override with the actual date being displayed in calendar
    };
    setSelectedClass(classWithDate);
    setDetailModalOpen(true);
  };

  const handleReservation = () => {
    setDetailModalOpen(false);
    if (onReservationClick) {
      onReservationClick(selectedClass);
    }
  };

  const generateCalendarDays = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Generate 7 days starting from Monday
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      
      days.push({
        day: date.getDate(),
        date,
        isOtherMonth: false,
        isToday: date.toDateString() === today.toDateString(),
        classes: getClassesForDate(date)
      });
    }

    return days;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get week date range for title
  const getWeekRange = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const startDay = startOfWeek.getDate();
    const endDay = endOfWeek.getDate();
    const startMonth = monthNames[startOfWeek.getMonth()];
    const endMonth = monthNames[endOfWeek.getMonth()];
    const year = endOfWeek.getFullYear();

    if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
      return `${startDay} - ${endDay} ${startMonth} ${year}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  };

  const calendarDays = generateCalendarDays();

  return (
    <>
      <CalendarContainer>
        <CalendarHeader>
          <NavigationButton onClick={goToPreviousWeek}>
            ← Önceki Hafta
          </NavigationButton>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <MonthTitle>
              {getWeekRange()}
            </MonthTitle>
            <NavigationButton onClick={goToToday} style={{ fontSize: '12px', padding: '4px 8px' }}>
              Bugün
            </NavigationButton>
          </div>
          <NavigationButton onClick={goToNextWeek}>
            Sonraki Hafta →
          </NavigationButton>
        </CalendarHeader>

        <WeekDays>
          {weekDayNames.map(day => (
            <WeekDay key={day}>{day}</WeekDay>
          ))}
        </WeekDays>

        <CalendarGrid>
          {calendarDays.map((dayData, index) => (
            <CalendarDay
              key={index}
              $isOtherMonth={dayData.isOtherMonth}
              $isToday={dayData.isToday}
            >
              <DayNumber>{dayData.day}</DayNumber>
              <ClassList>
                {dayData.classes.map((classItem, classIndex) => {
                  // Format date as YYYY-MM-DD
                  const displayDate = `${dayData.date.getFullYear()}-${String(dayData.date.getMonth() + 1).padStart(2, '0')}-${String(dayData.date.getDate()).padStart(2, '0')}`;
                  const hasReservation = hasActiveReservation(classItem.id, displayDate);
                  const isPast = isClassPast(displayDate, classItem.startTime || classItem.time);
                  return (
                    <ClassItem
                      key={classIndex}
                      isFull={classItem.isFull}
                      hasReservation={hasReservation}
                      $isPast={isPast}
                      onClick={() => !isPast && handleClassClick(classItem, displayDate)}
                      title={isPast ? 'Bu dersin saati geçti' : `${classItem.title} - ${classItem.startTime || classItem.time}${classItem.endTime ? `-${classItem.endTime}` : ''}`}
                    >
                      <ClassTitle>{classItem.title}</ClassTitle>
                      <ClassTime>
                        {classItem.startTime || classItem.time}
                        {classItem.endTime ? `-${classItem.endTime}` : ''}
                      </ClassTime>
                      {isPast && <PastBadge>⏰ Geçti</PastBadge>}
                    </ClassItem>
                  );
                })}
              </ClassList>
            </CalendarDay>
          ))}
        </CalendarGrid>
      </CalendarContainer>

      {selectedClass && (
        <Modal
          isOpen={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          title="Ders Detayları"
        >
          {selectedClass.instructorPhoto && (
            <div style={{ textAlign: 'center' }}>
              <InstructorPhoto 
                src={selectedClass.instructorPhoto} 
                alt={selectedClass.instructor}
              />
            </div>
          )}
          
          <ClassDetails>
            <DetailRow>
              <strong>📚 Ders:</strong> {selectedClass.title}
              {selectedClass.isFull && <StatusBadge type="full">Dolu</StatusBadge>}
              {!selectedClass.isFull && hasActiveReservation(selectedClass.id, selectedClass.date) && (
                <StatusBadge type="reserved">
                  {getReservationStatus(selectedClass.id, selectedClass.date) === 'pending' ? 'Onay Bekleniyor' : 'Rezerve Edildi'}
                </StatusBadge>
              )}
              {!selectedClass.isFull && !hasActiveReservation(selectedClass.id, selectedClass.date) && (
                <StatusBadge type="available">Müsait</StatusBadge>
              )}
            </DetailRow>
            
            {selectedClass.description && (
              <DetailRow>
                <strong>📝 Açıklama:</strong> {selectedClass.description}
              </DetailRow>
            )}
            
            <DetailRow>
              <strong>👨‍🏫 Eğitmen:</strong> {selectedClass.instructor}
            </DetailRow>
            
            {selectedClass.isOnline ? (
              <DetailRow>
                <strong>💻 Online Ders</strong>
                {selectedClass.onlineLink && hasActiveReservation(selectedClass.id, selectedClass.date) && getReservationStatus(selectedClass.id, selectedClass.date) === 'approved' && (
                  <>
                    {' - '}
                    <LocationLink 
                      href={selectedClass.onlineLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Derse Katıl
                    </LocationLink>
                  </>
                )}
              </DetailRow>
            ) : selectedClass.location && (
              <DetailRow>
                <strong>📍 Adres:</strong>{' '}
                <LocationLink 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedClass.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedClass.location}
                </LocationLink>
              </DetailRow>
            )}
            
            <DetailRow>
              <strong>📅 Tarih:</strong> {selectedClass.date}
              {selectedClass.isRecurring && ' (Tekrarlı)'}
            </DetailRow>
            
            <DetailRow>
              <strong>🕐 Saat:</strong> {selectedClass.startTime || selectedClass.time}
              {selectedClass.endTime && ` - ${selectedClass.endTime}`}
            </DetailRow>
            
            <DetailRow>
              <strong>👥 Kontenjan:</strong> {selectedClass.currentBookings} / {selectedClass.capacity} Dolu
            </DetailRow>
            
            <DetailRow>
              <strong>🪑 Boş Yer:</strong> {selectedClass.availableSpots}
            </DetailRow>
          </ClassDetails>

          {!hasActiveReservation(selectedClass.id, selectedClass.date) && !selectedClass.isFull && (
            <Button fullWidth onClick={handleReservation}>
              Rezervasyon Yap
            </Button>
          )}

          {hasActiveReservation(selectedClass.id, selectedClass.date) && (
            <Button fullWidth variant="success" disabled>
              {getReservationStatus(selectedClass.id, selectedClass.date) === 'pending' ? '⏳ Onay Bekleniyor' : '✓ Rezerve Edildi'}
            </Button>
          )}

          {selectedClass.isFull && !hasActiveReservation(selectedClass.id, selectedClass.date) && (
            <Button fullWidth variant="secondary" disabled>
              Dolu
            </Button>
          )}
        </Modal>
      )}
    </>
  );
};

